<template>
  <div class="row">
    <!-- Search Bar (Renders dynamically based on searchFields config) -->
    <div v-if="showSearchBar && searchFields.length" class="col-12">
      <div class="card">
        <div class="card-body">
          <div class="row">
            <div
              v-for="search in searchFields"
              :key="search.field"
              class="col-lg-2 col-md-6 mb-2"
            >
              <div class="input-group">
                <input
                  class="form-control"
                  :placeholder="search.label"
                  type="text"
                  @input="searchField(search.field, $event.target.value)"
                />
              </div>
            </div>
            <div class="col-lg-2 col-md-6 ms-auto">
              <a
                class="btn btn-sm btn-primary btn-block"
                href="javascript:void(0);"
              >
                Search
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dynamic Table -->
    <div class="col-12">
      <div class="card">
        <div class="card-body table-responsive">
          <table
            class="table table-hover table-custom dataTable vessels-js-data-table"
          >
            <thead>
              <tr>
                <th v-if="hasExpiredCertificates"></th>
                <th
                  v-for="col in activeColumns"
                  :key="col.key"
                  :title="col.title"
                >
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(vessel, index) in filteredVessels"
                :key="index"
                class="cursor-pointer"
                @click="openVesselDetails(vessel)"
              >
                <!-- Certificate Status Icon -->
                <td
                  v-if="hasExpiredCertificates"
                  :data-order="vessel.isCertExpired ?? '0'"
                  :title="getExpiryLevelText(vessel.isCertExpired)"
                >
                  <i
                    v-if="vessel.isCertExpired"
                    :class="`${getExpiryLevelColorClass(
                      vessel.isCertExpired
                    )} ri-error-warning-line`"
                  />
                </td>

                <!-- Dynamic Columns -->
                <td
                  v-for="col in activeColumns"
                  :key="col.key"
                  :class="{
                    'text-nowrap':
                      col.key === 'due_open' || col.key === 'due_close',
                  }"
                >
                  <!-- Custom rendering for H&M status boolean -->
                  <template v-if="col.key === 'h_and_m'">
                    <i
                      v-if="vessel.h_and_m"
                      class="ri-checkbox-blank-circle-fill text-success"
                    ></i>
                    <i
                      v-else
                      class="ri-checkbox-blank-circle-line text-secondary"
                    ></i>
                  </template>

                  <!-- Standard dynamic value fallback -->
                  <template v-else>
                    {{ getColumnValue(vessel, col) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getExpiryLevelColorClass,
  getExpiryLevelText,
  WARN,
} from "@/helpers/certificates";

export default {
  name: "VesselsTable",
  props: {
    vessels: {
      type: Array,
      default: () => [],
    },
    showSearchBar: {
      type: Boolean,
      default: false,
    },
    // Modes: 'standard', 'ncv', 'cvc', or pass custom 'columns' array directly
    mode: {
      type: String,
      default: "standard",
    },
    customColumns: {
      type: Array,
      default: null,
    },
    searchFields: {
      type: Array,
      default: () => [
        { label: "Name", field: "name" },
        { label: "IMO Number", field: "imo_number" },
        { label: "Official Number", field: "official_number" },
        { label: "S-Reg", field: "s_reg" },
      ],
    },
  },
  data() {
    return {
      WARN,
      fieldFilters: [],
    };
  },
  computed: {
    hasExpiredCertificates() {
      return this.vessels.some((v) => v.isCertExpired);
    },
    filteredVessels() {
      let result = [...this.vessels];
      this.fieldFilters.forEach((element) => {
        if (element.searchString) {
          result = result.filter((v) =>
            v?.[element.field]
              ?.toLowerCase()
              ?.includes(element.searchString.toLowerCase())
          );
        }
      });
      return result;
    },
    // Resolves presets based on mode prop
    activeColumns() {
      if (this.customColumns) return this.customColumns;

      const columnPresets = {
        standard: [
          { label: "H&M", key: "h_and_m", title: "Hull & Machinery" },
          {
            label: "Status",
            key: "service_status",
            getValue: (v) => v.service_status || "",
          },
          { label: "Name", key: "name" },
          { label: "IMO Number", key: "imo_number" },
          {
            label: "Official Number",
            key: "official_number",
            getValue: (v) => v.official_number || v.imo_number,
          },
          { label: "V. Type", key: "vessel_type_text" },
          { label: "Len. Overall", key: "length_overall" },
          { label: "Length", key: "length" },
          { label: "GT", key: "gt", getValue: (v) => v.gt || v.gt_and_nt },
          { label: "Base Port", key: "base_port" },
        ],
        // Commercial
        commercial: [
          { label: "H&M", key: "h_and_m", title: "Hull & Machinery" },
          { label: "Name", key: "name" },
          { label: "Vessel Type", key: "vessel_type_text" },
          {
            label: "Official Number",
            key: "official_number",
            getValue: (v) => v.official_number || v.imo_number,
          },
          { label: "GT", key: "gt", getValue: (v) => v.gt || v.gt_and_nt },
          { label: "Base Port", key: "base_port" },
          {
            label: "Cert. Name",
            key: "certificate_name",
            getValue: (v) => v.primaryCertificate?.certificate || "",
          },
          {
            label: "Due Open",
            key: "due_open",
            getValue: (v) => v.primaryCertificate?.due_open || "",
          },
          {
            label: "Due Close",
            key: "due_close",
            getValue: (v) => v.primaryCertificate?.due_close || "",
          },
        ],
        // Table Requirement 2 (NCV)
        ncv: [
          { label: "Name", key: "name" },
          { label: "Vessel Type", key: "vessel_type_text" },
          {
            label: "Official Number",
            key: "official_number",
            getValue: (v) => v.official_number || v.imo_number,
          },
          { label: "GT", key: "gt", getValue: (v) => v.gt || v.gt_and_nt },
          {
            label: "Cert. Name",
            key: "certificate_name",
            getValue: (v) => v.primaryCertificate?.certificate || "",
          },
          {
            label: "Due Open",
            key: "due_open",
            getValue: (v) => v.primaryCertificate?.due_open || "",
          },
          {
            label: "Due Close",
            key: "due_close",
            getValue: (v) => v.primaryCertificate?.due_close || "",
          },
        ],
        // Table Requirement 3 (CVC)
        cvc: [
          { label: "Name", key: "name" },
          { label: "Vessel Type", key: "vessel_type_text" },
          {
            label: "Official Number",
            key: "official_number",
            getValue: (v) => v.official_number || v.imo_number,
          },
          { label: "Length Overall", key: "length_overall" },
          {
            label: "Owner / Manager",
            key: "owner_manager",
            getValue: (v) =>
              v.stakeholders?.find(
                (stakeholder) =>
                  stakeholder.type === "Owner" || stakeholder.type === "Manager"
              )?.name || "",
          },
          { label: "GT", key: "gt", getValue: (v) => v.gt || v.gt_and_nt },
          {
            label: "Cert. Name",
            key: "certificate_name",
            getValue: (v) => v.primaryCertificate?.certificate || "",
          },
          {
            label: "Due Open",
            key: "due_open",
            getValue: (v) => v.primaryCertificate?.due_open || "",
          },
          {
            label: "Due Close",
            key: "due_close",
            getValue: (v) => v.primaryCertificate?.due_close || "",
          },
        ],
      };
      return columnPresets[this.mode] || columnPresets.standard;
    },
  },
  methods: {
    getExpiryLevelColorClass,
    getExpiryLevelText,
    openVesselDetails(vessel) {
      let resolvedRoute = this.$router.resolve({
        name: "vessels-show",
        params: { vesselId: vessel.id },
      });
      window.open(resolvedRoute.href, "_blank", "noreferrer");
    },
    getColumnValue(vessel, col) {
      if (typeof col.getValue === "function") {
        return col.getValue(vessel);
      }
      return vessel[col.key] || "";
    },
    searchField(field, searchString) {
      let elem = this.fieldFilters.find((f) => f.field === field);
      if (elem !== undefined) {
        elem.searchString = searchString;
      } else {
        this.fieldFilters.push({ field, searchString });
      }
    },
    buildDataTable() {
      if (this.table) this.table.destroy();
      setTimeout(() => {
        this.table = window.jsDataTable(".vessels-js-data-table", {
          columnDefs: [
            {
              targets: this.hasExpiredCertificates ? [0, 1] : 0,
              type: "numeric",
            },
          ],
          dom: '<"row"<"pl-2 col-lg-4"l><"text-center col-lg-4"B><"pr-2 col-lg-4"fr>>t<"row"<"pl-2 col-md-6"i><"pr-2 col-md-6"p>>',
          buttons: [
            {
              extend: "copy",
              className: "btn btn-sm btn-dt-action",
            },
            {
              extend: "csv",
              className: "btn btn-sm btn-dt-action",
            },
            {
              extend: "excel",
              className: "btn btn-sm btn-dt-action",
            },
            {
              extend: "pdf",
              className: "btn btn-sm btn-dt-action",
            },
            {
              extend: "print",
              className: "btn btn-sm btn-dt-action",
            },
          ],
          order: [],
          pageLength: 100,
          destroy: true,
        });
      }, 250); // Just giving Vue some time to render the table
    },
  },
  watch: {
    vessels() {
      this.buildDataTable();
    },
    mode() {
      this.buildDataTable();
    },
  },
  mounted() {
    this.buildDataTable();
  },
};
</script>
